import { AuthService } from './../../../services/auth/auth.service';
import { Component, computed, effect, inject, input, signal } from '@angular/core';
import { UploadedFile } from '../../../models/server-models/entities/uploaded-file';
import { ExpandedPrintVariation } from '../../../models/client-models/entities/expanded-print-variation';
import { ExpandedPaperSize } from '../../../models/client-models/entities/expanded-paper-size';
import { ExpandedPaperType } from '../../../models/client-models/entities/expanded-paper-type';
import { PaperSideType } from '../../../models/server-models/enums/paper-side-type';
import { ExpandedPaperExtra } from '../../../models/client-models/entities/expanded-paper-extra';
import { PaperColorType } from '../../../models/server-models/enums/paper-color-type';
import { PrintOfficeWithOfferDto } from '../print-office-selection/print-office-selection.component';
import { LoadingController, Platform } from '@ionic/angular';
import { PaymentMethod } from '../../../models/server-models/enums/payment-method';
import { ToastService } from '../../../services/toast.service';
import { PrintOrderService } from '../../../services/print-order.service';
import { OfferFile } from '../../../models/server-models/request-dtos/get-offices-offers-request-dto';
import { HttpErrorResponse } from '@angular/common/http';
import { ErrorService } from '../../../../../shared/services/error.service';
import { ApplePay } from '../../../../plugins/apple-pay.plugin';
import { Router } from '@angular/router';
import { DefaultSystemBrowserOptions, InAppBrowser } from '@capacitor/inappbrowser';
import { Capacitor } from '@capacitor/core';
import { PaymentStatus } from '../../../models/server-models/enums/payment-status';
import { GooglePay } from '../../../../plugins/google-pay.plugin';
import { LoggerService } from '../../../../../shared/services/logger.service';
import { TranslateService } from '@ngx-translate/core';
import { GeneralEventsService } from '../../../services/in-app-services/general-events.service';
import { BrowserService } from '../../../services/in-app-services/browser.service';

@Component({
  selector: 'app-payment-selection',
  templateUrl: './payment-selection.component.html',
  styleUrls: ['./payment-selection.component.scss'],
  standalone: false,
})
export class PaymentSelectionComponent {
  toastService = inject(ToastService);
  loadingCtrl = inject(LoadingController);
  printOrderService = inject(PrintOrderService);
  errorService = inject(ErrorService);
  router = inject(Router);
  loggerService = inject(LoggerService);
  translateService = inject(TranslateService);
  authService = inject(AuthService);
  generalEventsService = inject(GeneralEventsService);
  browserService = inject(BrowserService);

  selectedVariation = input.required<ExpandedPrintVariation | null>();
  selectedPaperSize = input.required<ExpandedPaperSize | null>();
  selectedPaperType = input.required<ExpandedPaperType | null>();
  selectedPaperSideType = input.required<PaperSideType | null>();
  selectedPaperColorType = input.required<PaperColorType | null>();
  selectedExtras = input.required<ExpandedPaperExtra[]>();
  uploadedFiles = input.required<UploadedFile[]>();
  selectedOffice = input.required<PrintOfficeWithOfferDto | null>();

  isIOS = signal<boolean>(false);
  isAndroid = signal<boolean>(false);
  isWeb = signal<boolean>(false);

  hasMobilePhoneProvided = computed(() => !!this.authService.startPrintUser()?.mobilePhone);

  paymentMethod = PaymentMethod;

  isButtonDisabled = signal<boolean>(true);
  comment = signal<string>('');

  printOrderId = signal<string | null>(null);

  constructor() {
    this.detectPlatform();
  }

  ngAfterViewInit(): void {
    if (!window['google']) {
      this.loggerService.logException(new Error('Google Pay script not loaded'), 'Google Pay Error');
      return;
    }

    const paymentsClient = new google.payments.api.PaymentsClient({ environment: 'PRODUCTION' });

    const button = paymentsClient.createButton({
      onClick: () => this.createOrder(PaymentMethod.GooglePay),
      buttonColor: 'default',
      buttonType: 'buy',
      buttonSizeMode: 'fill',
      buttonRadius: 8,
    });

    button.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
    });

    const container = document.getElementById('google-pay-button-container');
    if (container) {
      container.innerHTML = '';
      container.appendChild(button);
    }
  }

  detectPlatform() {
    this.isIOS.set(Capacitor.getPlatform() == 'ios');
    this.isAndroid.set(Capacitor.getPlatform() == 'android');
    this.isWeb.set(Capacitor.getPlatform() == 'web');
  }

  openGoogleMaps() {
    this.browserService.openUrl(this.selectedOffice()!.googleMapUrl);
  }

  async createOrder(selectedPaymentMethod: PaymentMethod) {
    // if (!this.hasMobilePhoneProvided()) {
    //   this.toastService.error(this.translateService.instant('PAYMENT.ERROR.MOBILE_PHONE_NOT_PROVIDED'));
    //   return;
    // }

    let token: string | null = '';

    switch (selectedPaymentMethod) {
      case PaymentMethod.ApplePay:
        token = await this.presentApplePay();
        break;
      case PaymentMethod.GooglePay:
        token = await this.presentGooglePay();
        break;
    }

    if (selectedPaymentMethod != PaymentMethod.Card && token == null) {
      this.toastService.error(this.translateService.instant('PAYMENT.ERROR.PAY_WITH_CARD'));
      return;
    }

    await this.showLoading(this.translateService.instant('PAYMENT.PLEASE_WAIT'));

    try {
      this.printOrderService
        .createOrder({
          printVariationId: this.selectedVariation()!.id,
          paperSizeId: this.selectedPaperSize()!.id,
          paperTypeId: this.selectedPaperType()!.id,
          paperSideType: this.selectedPaperSideType()!,
          paperColorType: this.selectedPaperColorType()!,
          paperExtraIds: this.selectedExtras().map((extra) => extra.id),
          offerFiles: this.uploadedFiles().map(
            (f) =>
              <OfferFile>{
                uniqueFileName: f.uniqueFileName,
                copies: f.copies,
              }
          ),
          printOfficeId: this.selectedOffice()!.id,
          paymentMethod: selectedPaymentMethod,
          comment: this.comment(),
          applePayToken: selectedPaymentMethod == PaymentMethod.ApplePay ? token : null,
          googlePayToken: selectedPaymentMethod == PaymentMethod.GooglePay ? token : null,
          isMobilePlatform: Capacitor.isNativePlatform(),
        })
        .subscribe({
          next: (responseDto) => {
            this.stopLoading();
            this.printOrderId.set(responseDto.printOrderId);

            if (responseDto.redirectLink) {
              // It's card payment
              this.openCardPaymentPage(responseDto.redirectLink);
            } else {
              // It's either Apple Pay or Google Pay
              this.redirectToPaymentReceivedPage();
            }
          },
          error: async (error: HttpErrorResponse) => {
            this.errorService.handleError(error);
            await this.stopLoading();
          },
        });
    } catch (error) {
      this.loggerService.logException(error, 'Payment Error');
      this.toastService.error(this.translateService.instant('PAYMENT.ERROR.PAYMENT_FAILED'));
      await this.stopLoading();
    }
  }

  private async presentApplePay(): Promise<string | null> {
    try {
      let price = this.selectedOffice()!.offerDetails.totalPrice;

      const totalPrice = Number(price).toFixed(2); // ensures "12.00" format

      const result = await ApplePay.presentApplePay({
        totalPrice: totalPrice,
      });

      return result.token;
    } catch (error) {
      this.loggerService.logException(error, 'Apple Pay Error');
      console.error('Apple Pay Error:', error);
      return null;
    }
  }

  async presentGooglePay(): Promise<string | null> {
    try {
      let price = this.selectedOffice()!.offerDetails.totalPrice;

      const totalPrice = Number(price).toFixed(2); // ensures "12.00" format

      const result = await GooglePay.presentGooglePay({
        totalPrice: totalPrice,
      });

      return result.token;
    } catch (error) {
      this.loggerService.logException(error, 'Google Pay Error');
      console.error('Google Pay Error:', error);
      return null;
    }
  }

  private async showLoading(msg: string) {
    const loading = await this.loadingCtrl.create({
      message: msg,

      animated: true,
    });

    await loading.present();
  }

  private async openCardPaymentPage(redirectLink: string) {
    if (this.isWeb()) {
      window.location.href = redirectLink;
      return;
    }

    await InAppBrowser.openInSystemBrowser({
      url: redirectLink,
      options: DefaultSystemBrowserOptions,
    });

    // Periodically ping the server to check if the payment is completed
    // After 60 attemps per 5 second each, stop checking and redirect to fail
    const maxAttempts = 60;
    let attempts = 0;
    let interval: any;

    const browserListener = await InAppBrowser.addListener('browserClosed', async () => {
      clearInterval(interval);
    });

    interval = setInterval(async () => {
      attempts++;
      if (attempts >= maxAttempts) {
        await InAppBrowser.close();
        clearInterval(interval);
        await browserListener.remove();
        this.toastService.error(this.translateService.instant('PAYMENT.ERROR.PAYMENT_NOT_SUCCEEDED'));
      } else {
        // Check payment status
        this.printOrderService.getOrderPaymentStatus(this.printOrderId()!).subscribe({
          next: async (paymentStatus) => {
            if (paymentStatus == PaymentStatus.Paid) {
              clearInterval(interval);
              await browserListener.remove();
              await InAppBrowser.close();
              this.redirectToPaymentReceivedPage();
            }
          },
          error: (error) => {
            this.errorService.handleError(error);
            clearInterval(interval);
            browserListener.remove();
          },
        });
      }
    }, 5000);
  }

  private redirectToPaymentReceivedPage() {
    this.generalEventsService.emitHomePageResetEvent();
    this.router.navigate(['/home', 'payment-received']);
  }

  private stopLoading() {
    this.loadingCtrl.dismiss();
  }
}
